package com.karawek.core.auth.presenter

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.karawek.core.auth.model.AuthenticationInfo
import com.karawek.core.auth.model.OtpChannel
import com.karawek.core.auth.screen.AuthenticationEvent
import com.karawek.core.auth.screen.AuthenticationEventSink
import com.karawek.core.auth.screen.AuthenticationResult
import com.karawek.core.auth.screen.AuthenticationScreen
import com.karawek.core.auth.screen.AuthenticationState
import com.karawek.core.auth.screen.OtpVerificationResult
import com.karawek.core.auth.screen.OtpVerificationScreen
import com.karawek.core.auth.usecase.AuthenticateWithNativeUseCase
import com.karawek.core.auth.usecase.AuthenticateWithPasskeyUseCase
import com.karawek.core.auth.usecase.AuthenticateWithOAuthUseCase
import com.karawek.core.auth.usecase.GetAvailableAuthMethodsUseCase
import com.karawek.core.auth.usecase.SaveAuthSessionUseCase
import com.slack.circuit.codegen.annotations.CircuitInject
import com.slack.circuit.foundation.rememberAnsweringNavigator
import com.slack.circuit.runtime.Navigator
import com.slack.circuit.runtime.presenter.Presenter
import kotlinx.coroutines.launch
import me.tatarka.inject.annotations.Assisted
import me.tatarka.inject.annotations.Inject
import software.amazon.lastmile.kotlin.inject.anvil.AppScope

@CircuitInject(
    screen = AuthenticationScreen::class,
    scope = AppScope::class,
)
@Inject
class AuthenticationPresenter(
    @Assisted private val screen: AuthenticationScreen,
    @Assisted private val navigator: Navigator,
    private val saveAuthSession: SaveAuthSessionUseCase,
    private val getAvailableAuthMethods: GetAvailableAuthMethodsUseCase,
    private val authenticateWithOAuth: AuthenticateWithOAuthUseCase,
    private val authenticateWithNative: AuthenticateWithNativeUseCase,
    private val authenticateWithPasskey: AuthenticateWithPasskeyUseCase,
) : Presenter<AuthenticationState> {

    @Composable
    override fun present(): AuthenticationState {
        val scope = rememberCoroutineScope()

        var state by remember {
            mutableStateOf(
                AuthenticationState(
                    canCancel = screen.cancel != null,
                ),
            )
        }

        /* -----------------------------
         * Helpers
         * ----------------------------- */

        fun onAuthSuccess(
            result: AuthenticationInfo,
        ) {
            scope.launch {
                saveAuthSession(result.session)
                val successScreen = screen.success
                if (successScreen != null) {
                    navigator.goTo(
                        screen = successScreen,
                    )
                } else {
                    navigator.pop(
                        AuthenticationResult(
                            status = AuthenticationResult.Status.Authenticated,
                            userInfo = result.user,
                        ),
                    )
                }
            }
        }

        fun launchAuth(
            block: suspend () -> Result<AuthenticationInfo>,
        ) {
            scope.launch {
                state = state.copy(
                    isLoading = true,
                    otpErrorMessage = null,
                    generalErrorMessage = null,
                )

                block().fold(
                    onSuccess = { result ->
                        state = state.copy(
                            isLoading = false,
                        )
                        onAuthSuccess(
                            result = result,
                        )
                    },
                    onFailure = { throwable ->
                        state = state.copy(
                            isLoading = false,
                            generalErrorMessage = throwable.message,
                        )
                    },
                )
            }
        }

        lateinit var handleInitiateOtp: (OtpChannel, String) -> Unit

        val otpNavigator = rememberAnsweringNavigator<OtpVerificationResult>(navigator) { result ->
            when (result.status) {
                OtpVerificationResult.Status.Verified -> {
                    val successScreen = screen.success
                    if (successScreen != null) {
                        navigator.goTo(successScreen)
                    } else {
                        navigator.pop(
                            AuthenticationResult(
                                status = AuthenticationResult.Status.Authenticated,
                                userInfo = result.userInfo,
                            ),
                        )
                    }
                }
                OtpVerificationResult.Status.Cancelled -> {
                    val cancelScreen = screen.cancel
                    if (cancelScreen != null) {
                        navigator.goTo(cancelScreen)
                    } else {
                        navigator.pop(
                            AuthenticationResult(
                                status = AuthenticationResult.Status.Cancelled,
                            ),
                        )
                    }
                }
            }
        }

        handleInitiateOtp = { channel, destination ->
            state = state.copy(otpErrorMessage = null, generalErrorMessage = null)
            otpNavigator.goTo(
                OtpVerificationScreen(
                    channel = channel,
                    destination = destination,
                    loginOrCreate = true,
                ),
            )
        }

        /* -----------------------------
         * Event handling
         * ----------------------------- */

        fun onEvent(
            event: AuthenticationEvent,
        ) {
            when (event) {

                AuthenticationEvent.OnAppear -> {
                    scope.launch {
                        val methods = getAvailableAuthMethods()
                        state = state.copy(
                            availableMethods = methods,
                        )
                    }
                }

                is AuthenticationEvent.SelectOtpChannel -> {
                    state = state.copy(
                        selectedOtpChannel = event.channel,
                    )
                }

                is AuthenticationEvent.UpdateOtpDestination -> {
                    state = state.copy(
                        otpDestination = event.value,
                    )
                }

                AuthenticationEvent.StartOtp -> {
                    if (state.otpDestination.isBlank()) {
                        state = state.copy(otpErrorMessage = "Enter a destination first.")
                        return
                    }
                    // Phone numbers are already in E.164 format from CorePhoneTextField
                    handleInitiateOtp(state.selectedOtpChannel, state.otpDestination)
                }

                is AuthenticationEvent.StartOAuth -> {
                    state = state.copy(
                        otpErrorMessage = null,
                        generalErrorMessage = null,
                    )
                    launchAuth {
                        authenticateWithOAuth(event.provider)
                    }
                }

                is AuthenticationEvent.StartNativeAuth -> {
                    state = state.copy(
                        otpErrorMessage = null,
                        generalErrorMessage = null,
                    )
                    launchAuth {
                        authenticateWithNative()
                    }
                }

                is AuthenticationEvent.StartPasskey -> {
                    state = state.copy(
                        otpErrorMessage = null,
                        generalErrorMessage = null,
                    )
                    launchAuth {
                        authenticateWithPasskey()
                    }
                }

                AuthenticationEvent.Cancel -> {
                    val cancelScreen = screen.cancel
                    if (cancelScreen != null) {
                        navigator.goTo(
                            screen = cancelScreen,
                        )
                    } else {
                        navigator.pop(
                            AuthenticationResult(
                                status = AuthenticationResult.Status.Cancelled,
                            ),
                        )
                    }
                }

                AuthenticationEvent.DismissError -> {
                    state = state.copy(
                        otpErrorMessage = null,
                        generalErrorMessage = null,
                    )
                }
            }
        }

        return state.copy(
            eventSink = AuthenticationEventSink(::onEvent),
        )
    }
}
